import { Badge, Grid, Switch, TextInput } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { http } from "../../api/http";
import { ChoroplethMap } from "../../components/ChoroplethMap";
import { DataTable } from "../../components/DataTable";
import { PageHeader } from "../../components/ui";
import { useOpstina } from "../../context/opstina";
import AdminLayout from "./AdminLayout";

interface Unit { id: number; code: string; name: string; kind: string; region?: string; active: boolean; orchard_count: number; producer_count: number; area_ha: number; ko_count: number }

export default function AdminUnitsPage() {
  const qc = useQueryClient();
  const { setOpstina, opstina } = useOpstina();
  const units = useQuery({ queryKey: ["units-admin"], queryFn: () => http.get<Unit[]>("/api/v1/admin-units", { only_active: false }) });
  const geo = useQuery({ queryKey: ["units-geo"], queryFn: () => http.get<{ features: { properties: { code: string; name: string; area_ha: number } }[] }>("/api/v1/admin-units.geojson") });
  const patch = useMutation({ mutationFn: ({ id, body }: { id: number; body: Record<string, unknown> }) => http.patch(`/api/v1/admin-units/${id}`, body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["units-admin"] }); qc.invalidateQueries({ queryKey: ["admin-units-header"] }); },
    onError: (e: Error) => notifications.show({ color: "red", message: e.message }) });
  const fc = geo.data ? { ...geo.data, label: "ha", features: geo.data.features.map((f) => ({ ...f, properties: { ...f.properties, value: f.properties.area_ha } })) } : undefined;
  return (
    <AdminLayout>
      <>
        <PageHeader title="Opštine" description="Opštine/gradovi Republike Srpske kao nivo upravljanja: preko njih se dodjeljuje nadležnost korisnicima i filtriraju podaci i izvještaji. Granice: geoBoundaries (ADM3), katastarske opštine povezane prostorno." />
        <Grid>
          <Grid.Col span={{ base: 12, lg: 7 }}>
            <DataTable rows={(units.data ?? []) as unknown as Record<string, unknown>[]} exportName="opstine" columns={[
              { key: "name", label: "Naziv" }, { key: "code", label: "Šifra" }, { key: "kind", label: "Vrsta", render: (r) => <Badge size="xs" variant="light">{String(r.kind)}</Badge> },
              { key: "region", label: "Područna jedinica", render: (r) => <TextInput size="xs" defaultValue={String(r.region ?? "")}
                onBlur={(e) => e.currentTarget.value !== (r.region ?? "") && patch.mutate({ id: Number(r.id), body: { region: e.currentTarget.value || null } })} /> },
              { key: "ko_count", label: "KO", type: "integer" }, { key: "producer_count", label: "Proizvođača", type: "integer" },
              { key: "orchard_count", label: "Voćnjaka", type: "integer" }, { key: "area_ha", label: "Površina (ha)", type: "number" },
              { key: "active", label: "Aktivna", render: (r) => <Switch size="xs" checked={!!r.active} onChange={(e) => patch.mutate({ id: Number(r.id), body: { active: e.currentTarget.checked } })} /> },
            ]} />
          </Grid.Col>
          <Grid.Col span={{ base: 12, lg: 5 }}>
            <ChoroplethMap data={fc} height={560} selected={opstina} onSelect={(c) => setOpstina(c === opstina ? null : c)} />
          </Grid.Col>
        </Grid>
      </>
    </AdminLayout>
  );
}
